import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';

import { mergeMap, take } from 'rxjs';
import { SpecialCharacters } from 'src/app/core/enums';
import { CODReconcile } from 'src/app/core/models/reports/cod-reconcile.model';
import { CodsReportsService } from 'src/app/core/services/reports/cod/cod-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { ReconcileComponent } from '../reconcile.component';
import { ColumnIds, ColumnText, CONSTS } from './reconcile-popup.constants';

@Component({
  selector: 'app-reconcile-popup',
  templateUrl: './reconcile-popup.component.html',
  styleUrls: ['./reconcile-popup.component.scss'],
  standalone: false,
})
export class ReconcilePopupComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly data = inject(MAT_DIALOG_DATA) as CODReconcile[];
  readonly commonUtils: CommonUtils = inject(CommonUtils);
  readonly reportUtils: ReportsUtils = inject(ReportsUtils);

  readonly datePipe: DatePipe = inject(DatePipe);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<ReconcileComponent> = inject(
    MatDialogRef<ReconcileComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private codReconcileReportService: CodsReportsService =
    inject(CodsReportsService);

  readonly CONSTS = CONSTS;
  readonly COLUMNTEXT = ColumnText;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly SpecialCharacters = SpecialCharacters;
  reconcileForm!: FormGroup;
  modalFormData!: FormGroup;

  ngOnInit(): void {
    this.initiateForm();
  }

  onExit() {
    this.commonUtils.openExitModal(this.reconcileForm.dirty);
  }

  onSubmit() {
    const filteredRows = this.controls
      .filter((data) => {
        return data.dirty && data.valid;
      })
      .map((data) => {
        return {
          ...data.value,
          codTurninDate: data.value.turnIn ? data.value.codTurninDate : null,
        };
      });

    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          return this.codReconcileReportService.editCodReconcile(
            user?.location,
            {
              codReconcile: filteredRows,
            },
          );
        }),
      )
      .subscribe(() => {
        this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        this.dialogRef.close();
      });
  }

  updateCheckbox(i: number) {
    const checkVal = this.controls[i].get('turnIn');
    const turnIn = this.controls[i].get('codTurninAmount');
    if (checkVal?.value === false) {
      turnIn?.addValidators(Validators.required);
      checkVal?.markAsTouched();
      checkVal?.markAsDirty();
    } else {
      turnIn?.removeValidators(Validators.required);
      checkVal?.markAsPristine();
      checkVal?.markAsUntouched();
    }
    checkVal?.setValue(!checkVal.value);
    checkVal?.updateValueAndValidity();
    turnIn?.updateValueAndValidity();
  }

  updateValidity(i: number, control: string) {
    const turnIn = this.controls[i].get(control);
    if (turnIn?.value.length === 0) {
      turnIn?.markAsPristine();
      turnIn?.markAsUntouched();
      turnIn?.updateValueAndValidity();
    }
  }

  isInvalid(i: number) {
    const amount = this.controls[i].get('codTurninAmount');
    return amount?.invalid;
  }

  get controls() {
    return (this.reconcileForm.get('data') as FormArray).controls;
  }

  private initiateForm(): void {
    this.reconcileForm = this.fb.group({
      data: this.fb.array(
        this.data.map((reconcileData: CODReconcile) =>
          this.fb.group({
            stopId: reconcileData.stopId,
            position: reconcileData.position,
            stopDate: reconcileData.stopDate,
            loop: reconcileData.loop,
            locationCode: reconcileData.locationCode,
            trackingNo: reconcileData.trackingNo,
            shipper: reconcileData.shipper,
            pmntAmount: reconcileData.pmntAmount,
            pmntCurrency: reconcileData.pmntCurrency,
            pmntType: reconcileData.pmntType,
            stopType: reconcileData.stopType,
            turnIn: [false],
            codTurninAmount: [reconcileData.codTurninAmount],
            codTurninComments: [reconcileData.codTurninComments],
            codTurninDate: this.datePipe.transform(new Date(), 'MM-dd-yyyy'),
          }),
        ),
      ),
    });

    this.controls.forEach((val) => {
      if (val.get('pmntType')?.value === null) {
        val.disable();
      }
    });
  }

  downloadCSV() {
    // Guard: no data
    if (!Array.isArray(this.data) || this.data.length === 0) {
      this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
      return;
    }

    // Your desired headings (order matters)
    const CSV_FILE_COLUMN_HEADING = [
      'Stop Date',
      'Loop',
      'Tracking Number',
      'Shipper',
      'Pay Amt',
      'Pay Type',
      'Stop Type',
    ];

    // 1) Map each record to an object that has ONLY those 7 keys
    //    in EXACTLY this insertion order.
    //    This ensures the util writes columns in this order.
    const projectedRows = this.data.map((r: CODReconcile) => ({
      'Stop Date': r.stopDate ?? '',
      Loop: r.loop ?? '',
      'Tracking Number': r.trackingNo ?? '',
      Shipper: r.shipper ?? '',
      'Pay Amt': r.pmntAmount != null ? String(r.pmntAmount) : '',
      'Pay Type': r.pmntType ?? '',
      'Stop Type': r.stopType ?? '',
    }));

    // 2) Force the util to include a header row (it ignores the actual header content
    //    and only checks 'truthy'), so we pass a dummy 2D array.
    const dummyHeader: string[][] = [[]];

    // 3) No custom footer needed for this requirement (you can pass one if desired)
    const customFooter: string[][] | null = null;

    // 4) Export using the existing util
    this.reportUtils.exportCSVReport(
      projectedRows as any, // T[]: plain objects keyed by our headings
      'cod-reconcile', // file name (without extension)
      dummyHeader, // truthy -> util adds a header row
      customFooter, // null for now
      CSV_FILE_COLUMN_HEADING, // our exact headings row
    );
  }
}
